/**
 * LegalTranslationCreatePage — form for creating a new legal translation.
 *
 * Route: /dashboard/cloudgm/legal-translation/new
 *
 * Sections:
 *   1) Language pair (source + target) with quick-flip button
 *   2) Input mode toggle (Text | Document)
 *   3) Text area OR File upload (PDF / image)
 *   4) Optional doc type + notes
 *   5) Submit → Gemini streaming translation, shown on the same page
 *   6) After success: download PDF, copy, or make another
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Languages, FileText, Type, ArrowLeftRight, Upload, X,
  Copy, FileDown, Sparkles, CheckCircle2, Image as ImageIcon,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { key: 'passport',            ar: 'جواز سفر',           en: 'Passport' },
  { key: 'emirates_id',         ar: 'هوية إماراتية',       en: 'Emirates ID' },
  { key: 'driving_licence',     ar: 'رخصة قيادة',         en: 'Driving licence' },
  { key: 'birth_certificate',   ar: 'شهادة ميلاد',        en: 'Birth certificate' },
  { key: 'marriage_certificate',ar: 'شهادة زواج',         en: 'Marriage certificate' },
  { key: 'commercial_licence',  ar: 'رخصة تجارية',        en: 'Commercial licence' },
  { key: 'court_document',      ar: 'وثيقة قضائية',       en: 'Court document' },
  { key: 'contract',            ar: 'عقد',               en: 'Contract' },
  { key: 'medical_report',      ar: 'تقرير طبي',          en: 'Medical report' },
  { key: 'academic_certificate',ar: 'شهادة أكاديمية',      en: 'Academic certificate' },
  { key: 'other',               ar: 'أخرى',              en: 'Other' },
];

const MAX_FILE_MB = 10;

export default function LegalTranslationCreatePage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [languages, setLanguages] = useState([]);
  const [form, setForm] = useState({
    source_lang: 'ar',
    target_lang: 'en',
    mode: 'text',
    text: '',
    file_data_url: null,
    file_mime_type: null,
    file_name: null,
    doc_type: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/legal-translation/languages`, { headers: getAuthHeaders() })
      .then((r) => setLanguages(r.data.data || []))
      .catch(() => toast.error(t('فشل تحميل اللغات', 'Failed to load languages')));
  }, [isAr]);

  const swapLanguages = () => {
    setForm(f => ({ ...f, source_lang: f.target_lang, target_lang: f.source_lang }));
  };

  const onFilePicked = useCallback(async (fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    const accept = /^(application\/pdf|image\/(png|jpe?g|webp))$/i;
    if (!accept.test(f.type)) {
      toast.error(t('نوع غير مدعوم — PDF أو صور فقط', 'Unsupported — PDF or images only'));
      return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(t(`الحد الأقصى ${MAX_FILE_MB} ميجا`, `Max ${MAX_FILE_MB} MB`));
      return;
    }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    setForm(fState => ({
      ...fState,
      file_data_url: dataUrl,
      file_mime_type: f.type,
      file_name: f.name,
    }));
  }, [isAr]);

  const removeFile = () => setForm(f => ({
    ...f, file_data_url: null, file_mime_type: null, file_name: null,
  }));

  const canSubmit = form.source_lang && form.target_lang && form.source_lang !== form.target_lang
    && (form.mode === 'text' ? !!form.text.trim() : !!form.file_data_url)
    && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setResult(null);
    try {
      const body = {
        source_lang: form.source_lang,
        target_lang: form.target_lang,
        mode: form.mode,
        text: form.mode === 'text' ? form.text : null,
        file_data_url: form.mode === 'document' ? form.file_data_url : null,
        file_mime_type: form.mode === 'document' ? form.file_mime_type : null,
        file_name: form.mode === 'document' ? form.file_name : null,
        doc_type: form.doc_type || null,
        notes: form.notes || null,
      };
      const r = await axios.post(`${API_URL}/api/admin/legal-translation`, body, {
        headers: getAuthHeaders(),
        timeout: 120000,   // Gemini vision on a full document can take 30-60s
      });
      setResult(r.data);
      toast.success(t('✓ اكتملت الترجمة', '✓ Translation complete'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشلت الترجمة', 'Translation failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyResult = () => {
    navigator.clipboard.writeText(result?.translated_text || '')
      .then(() => toast.success(t('تم النسخ', 'Copied')));
  };

  const downloadPdf = () => {
    if (!result?.id) return;
    const token = getAuthHeaders().Authorization?.replace(/^Bearer /, '') || '';
    const url = `${API_URL}/api/admin/legal-translation/${result.id}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const reset = () => {
    setResult(null);
    setForm(f => ({ ...f, text: '', file_data_url: null, file_mime_type: null, file_name: null, notes: '' }));
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#10b981]/6 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/cloudgm/legal-translation" />

      <div className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=platform-control&sub=fzlc' },
            { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/manage/cloudgm' },
            { label_ar: 'طلبات الخدمة', label_en: 'Service Requests', path: '/manage/cloudgm?tab=services-categories' },
            { label_ar: 'الترجمة القانونية (AI)', label_en: 'Legal Translation (AI)', path: '/dashboard/cloudgm/legal-translation' },
            { label_ar: 'ترجمة جديدة', label_en: 'New', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
            <Languages className="w-7 h-7 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
              {t('ترجمة قانونية جديدة', 'New Legal Translation')}
            </h1>
            <p className="text-white/50 text-sm">
              {t('اختر لغتي المصدر والهدف، ثم أدخل النص أو ارفع صورة المستند. Gemini يفهم الوثائق ويحافظ على التنسيق.',
                 'Pick source & target languages, then type the text or upload the document. Gemini reads the layout and preserves the structure.')}
            </p>
          </div>
        </div>

        {!result ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-6">
            {/* Language pair */}
            <section>
              <h3 className="text-white font-bold text-sm mb-3">
                {t('١) اختر اللغتين *', '1) Language pair *')}
              </h3>
              <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                <Field label={t('من', 'From')}>
                  <select value={form.source_lang}
                          onChange={(e) => setForm(f => ({ ...f, source_lang: e.target.value }))}
                          dir={isAr ? 'rtl' : 'ltr'}
                          className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-11 px-3 text-sm"
                          data-testid="lt-source-lang">
                    {languages.map(l => (
                      <option key={l.code} value={l.code}>{isAr ? l.ar : l.en}</option>
                    ))}
                  </select>
                </Field>
                <button onClick={swapLanguages}
                        title={t('تبديل', 'Swap')}
                        className="h-11 w-11 rounded-md bg-white/5 hover:bg-emerald-500/15 border border-white/10 hover:border-emerald-500/40 flex items-center justify-center transition"
                        data-testid="lt-swap-btn">
                  <ArrowLeftRight className="w-4 h-4 text-emerald-300" />
                </button>
                <Field label={t('إلى', 'To')}>
                  <select value={form.target_lang}
                          onChange={(e) => setForm(f => ({ ...f, target_lang: e.target.value }))}
                          dir={isAr ? 'rtl' : 'ltr'}
                          className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-11 px-3 text-sm"
                          data-testid="lt-target-lang">
                    {languages.map(l => (
                      <option key={l.code} value={l.code}>{isAr ? l.ar : l.en}</option>
                    ))}
                  </select>
                </Field>
              </div>
              {form.source_lang === form.target_lang && (
                <div className="text-amber-400 text-xs mt-2">
                  {t('اختر لغتين مختلفتين', 'Pick two different languages')}
                </div>
              )}
            </section>

            {/* Mode toggle */}
            <section>
              <h3 className="text-white font-bold text-sm mb-3">
                {t('٢) نوع المدخل', '2) Input type')}
              </h3>
              <div className="flex items-center gap-2 bg-[#050505] border border-white/10 rounded-lg p-1 max-w-md">
                <button
                  onClick={() => setForm(f => ({ ...f, mode: 'text' }))}
                  data-testid="lt-mode-text"
                  className={`flex-1 h-10 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition ${
                    form.mode === 'text' ? 'bg-emerald-500/25 text-white' : 'text-white/50 hover:text-white/80'
                  }`}>
                  <Type className="w-4 h-4" /> {t('نص', 'Text')}
                </button>
                <button
                  onClick={() => setForm(f => ({ ...f, mode: 'document' }))}
                  data-testid="lt-mode-document"
                  className={`flex-1 h-10 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition ${
                    form.mode === 'document' ? 'bg-emerald-500/25 text-white' : 'text-white/50 hover:text-white/80'
                  }`}>
                  <FileText className="w-4 h-4" /> {t('مستند (PDF / صورة)', 'Document (PDF / image)')}
                </button>
              </div>
            </section>

            {/* Input */}
            <section>
              <h3 className="text-white font-bold text-sm mb-3">
                {t('٣) المحتوى المراد ترجمته *', '3) Content to translate *')}
              </h3>
              {form.mode === 'text' ? (
                <div>
                  <textarea
                    value={form.text}
                    onChange={(e) => setForm(f => ({ ...f, text: e.target.value }))}
                    rows={10}
                    placeholder={t('الصق النص القانوني هنا...', 'Paste the legal text here...')}
                    className="w-full bg-[#050505] border border-white/10 text-white rounded-md p-3 text-sm resize-y focus:outline-none focus:border-emerald-500/50"
                    dir="auto"
                    data-testid="lt-text-input" />
                  <div className="text-[11px] text-white/40 mt-1">
                    {form.text.length.toLocaleString()} / 100,000 {t('حرف', 'characters')}
                  </div>
                </div>
              ) : (
                <div>
                  {!form.file_data_url ? (
                    <label htmlFor="lt-file-input"
                           className="flex flex-col items-center justify-center gap-3 h-40 border-2 border-dashed border-white/15 hover:border-emerald-500/50 hover:bg-emerald-500/5 rounded-xl cursor-pointer transition"
                           data-testid="lt-file-drop">
                      <Upload className="w-8 h-8 text-emerald-400/60" />
                      <div className="text-white/80 text-sm">
                        {t('اسحب المستند هنا أو انقر للاختيار', 'Drag the document here or click to select')}
                      </div>
                      <div className="text-white/40 text-xs">
                        {t(`PDF أو PNG أو JPEG أو WebP · حتى ${MAX_FILE_MB} ميجا`,
                           `PDF or PNG or JPEG or WebP · up to ${MAX_FILE_MB} MB`)}
                      </div>
                    </label>
                  ) : (
                    <div className="flex items-center gap-3 bg-[#050505] border border-emerald-500/40 rounded-lg p-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded bg-emerald-500/15 flex items-center justify-center">
                        {form.file_mime_type?.startsWith('image/')
                          ? <ImageIcon className="w-6 h-6 text-emerald-300" />
                          : <FileText className="w-6 h-6 text-emerald-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm truncate">{form.file_name}</div>
                        <div className="text-white/40 text-xs">{form.file_mime_type}</div>
                      </div>
                      <button onClick={removeFile}
                              className="p-2 rounded hover:bg-red-500/10 text-red-400"
                              data-testid="lt-file-remove">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <input id="lt-file-input" type="file" hidden
                         accept="application/pdf,image/png,image/jpeg,image/webp"
                         onChange={(e) => { onFilePicked(e.target.files); e.target.value = ''; }} />
                </div>
              )}
            </section>

            {/* Optional doc type + notes */}
            <section>
              <h3 className="text-white font-bold text-sm mb-3">
                {t('٤) تفاصيل إضافية (اختياري)', '4) Extra details (optional)')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label={t('نوع المستند', 'Document type')}>
                  <select value={form.doc_type}
                          onChange={(e) => setForm(f => ({ ...f, doc_type: e.target.value }))}
                          dir={isAr ? 'rtl' : 'ltr'}
                          className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 px-2 text-sm"
                          data-testid="lt-doc-type">
                    <option value="">{t('— حدد نوع المستند —', '— Pick doc type —')}</option>
                    {DOC_TYPES.map(d => (
                      <option key={d.key} value={d.key}>{isAr ? d.ar : d.en}</option>
                    ))}
                  </select>
                </Field>
                <Field label={t('ملاحظات للمُترجم', 'Notes to the translator')}>
                  <Input value={form.notes}
                         onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                         placeholder={t('مثال: احتفظ برقم الجواز كما هو',
                                         'e.g. keep passport number as-is')}
                         className="bg-[#050505] border-white/10 text-white"
                         data-testid="lt-notes" />
                </Field>
              </div>
            </section>

            {/* Submit */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Button variant="outline"
                      onClick={() => navigate('/dashboard/cloudgm/legal-translation')}
                      className="border-white/10 bg-transparent text-white/80 hover:bg-white/5 h-12"
                      data-testid="lt-cancel">
                <ArrowLeft className="w-4 h-4 me-2" />
                {t('إلغاء والعودة', 'Cancel & back')}
              </Button>
              <Button onClick={submit} disabled={!canSubmit}
                      className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-bold h-12 disabled:opacity-50"
                      data-testid="lt-submit">
                {submitting
                  ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ الترجمة...', 'Translating...')}</>
                  : <><Sparkles className="w-4 h-4 me-2" />{t('ترجمة بالذكاء الاصطناعي', 'Translate with AI')}</>}
              </Button>
            </div>
            {submitting && (
              <div className="text-[11px] text-emerald-400/80 text-center animate-pulse">
                {t('قد تستغرق ترجمة مستند مصور 30-60 ثانية...',
                   'Translating a scanned document can take 30-60 seconds...')}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white/[0.02] border border-emerald-500/30 rounded-2xl p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-300" />
              </div>
              <div>
                <div className="text-white font-bold text-lg">{t('✓ اكتملت الترجمة', '✓ Translation complete')}</div>
                <div className="text-white/50 text-xs font-mono">{result.id}</div>
              </div>
            </div>

            <div className="bg-[#050505] border border-white/10 rounded-lg p-4">
              <div className="text-white/50 text-xs mb-2">{t('النتيجة', 'Result')}</div>
              <div className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir="auto"
                   data-testid="lt-result-text">
                {result.translated_text}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Button onClick={copyResult} variant="outline"
                      className="border-white/10 bg-transparent text-white h-11"
                      data-testid="lt-copy-btn">
                <Copy className="w-4 h-4 me-2" />
                {t('نسخ', 'Copy')}
              </Button>
              <Button onClick={downloadPdf} variant="outline"
                      className="border-white/10 bg-transparent text-white h-11"
                      data-testid="lt-pdf-btn">
                <FileDown className="w-4 h-4 me-2" />
                {t('تحميل PDF', 'Download PDF')}
              </Button>
              <Button onClick={reset}
                      className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-bold h-11"
                      data-testid="lt-again-btn">
                <Sparkles className="w-4 h-4 me-2" />
                {t('ترجمة أخرى', 'New translation')}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-white/70 text-xs mb-1.5">{label}</label>
      {children}
    </div>
  );
}
